(ns core.db.entity-api-mem
  "This is an implementation namespace for public API defined in `core.db.entity-api`. This
  implements the API using in-memory DB. Do not use this directly, the the public API instead."
  (:refer-clojure :exclude [find])
  (:require [clojure.string :as str]
            [potpuri.core :as p]))


;;
;; Entity:
;; ==============================================================
;;


;; This provides a same API as the `datomic.api/entity`, but uses
;; a in-memory "DB" as the data source.
;;
;; The DB is expected to be a map with three keys, the `:schema`,
;; `:components` and `:entity-db`.
;;
;; The `:schema` has a mapping from attributes of type `:db.type/ref` to
;; cardinality. Only attributes of type `:db.type/ref` must be present.
;; The value is either `:db.cardinality/one` or `:db.cardinality/many`.
;;
;; The `:entity-db` holds a map of known entities, keyed by entity-id. Values
;; are maps of attribute->value. It is expected that the attribute->value
;; map also contains the mapping for `:db/id` -> entity ID.
;;
;; `:components` holds a set of attributes with `:db/isComponent` set.
;; This information is necessary because reverse lookups on components
;; return a single element instead of a list.
;;
;; To distinguish between the instances of `Entity` (see below) and a the
;; attribute->value map from the DB, this namespace uses the term `entity`
;; for instances of `Entity` and `attr` for attribute->value map.
;;
;; TODO: More documentation


(declare value->entity lookup-entity equal-entities)

(defprotocol IRefOwner
  (-get-id [e attr]))

(deftype Entity [db entity-id attrs]
  #?@(:cljs
      [Object
       (toString [_] (pr-str* attrs))
       (equiv [this other] (equal-entities this other))

       IEquiv
       (-equiv [this other] (equal-entities this other))

       IHash
       (-hash [_] (hash entity-id))

       ISeqable
       (-seq [_] (for [[attr v] attrs]
                   (MapEntry. attr (value->entity db attr v) nil)))

       ICounted
       (-count [_] (count (dissoc attrs :app.front.entities/gen)))

       ILookup
       (-lookup [this attr] (lookup-entity this attr))
       (-lookup [this attr not-found] (lookup-entity this attr not-found))

       IAssociative
       (-contains-key? [this attr] (or (some? (lookup-entity this attr))
                                       ; Return true for attrs whose target entity is not in entity db
                                       (-> attrs attr some?)))
       (-assoc [this _attr _val] (throw (ex-info "Entity does not support IAssociative assoc"
                                                 {:this this})))

       IFn
       (-invoke [this attr] (lookup-entity this attr))
       (-invoke [this attr not-found] (lookup-entity this attr not-found))

       IPrintWithWriter
       (-pr-writer [_ writer opts] (-pr-writer attrs writer opts))

       IRefOwner
       (-get-id [this attr] (get (.-attrs this) attr))]

      :clj
      [Object
       (toString [_] (pr-str attrs))
       (hashCode [_] (hash entity-id))
       (equals [this other] (equal-entities this other))

       clojure.lang.ILookup
       (valAt [this attr] (lookup-entity this attr))
       (valAt [this attr not-found] (lookup-entity this attr not-found))

       clojure.lang.IFn
       (invoke [this attr] (lookup-entity this attr))
       (invoke [this attr not-found] (lookup-entity this attr not-found))

       clojure.lang.Seqable
       (seq [_] (for [[attr v] attrs]
                  [attr (value->entity db attr v)]))

       clojure.lang.Associative
       (containsKey [this attr] (some? (lookup-entity this attr)))
       (entryAt [this attr] (some->> (lookup-entity this attr)
                                     (clojure.lang.MapEntry. attr)))
       (assoc [_ _ _] (throw (UnsupportedOperationException.)))
       ; Associative -> IPersistentCollection
       (count [_] (count attrs))
       (equiv [this other] (equal-entities this other))
       (cons [_ _] (throw (UnsupportedOperationException.)))
       (empty [_] (throw (UnsupportedOperationException.)))]))


(defn entity? [v]
  (instance? Entity v))


(defn equal-entities [^Entity this ^Entity other]
  (or (identical? this other)
      (and (entity? other)
           (= (-> this .-entity-id)
              (-> other .-entity-id))
           ;; backend datomic.api entities are always equal regardless of what database they point to (and
           ;; consequently what their contents are), but in the frontend we need to make reagent/re-frame
           ;; notice when our entity *contents* change,  including the meta-attribute :app.front.entities/gen
           ;; - that changes any time any entity that can be navigated to from a specific entity changes,
           ;; which means anything dependent on that entity must be re-rendered just in case
           (= (.-attrs this) (.-attrs other)))))


(defn- attrs->entity [db attrs]
  (when-let [entity-id (:db/id attrs)]
    (Entity. db entity-id (dissoc attrs :db/id))))


(defn entity [db entity-id]
  (attrs->entity db (-> db :entity-db (get entity-id))))


(defn- value->entity [db attr v]
  (cond
    ; Some entities are exported directly as maps
    (map? v) v
    (and (coll? v)
         (-> v first map?)) v
    (some? v) (case (-> db :schema attr)
                :db.cardinality/one (entity db v)
                :db.cardinality/many (map #(entity db %) v)
                nil v)))


(defn reverse-lookup-attr? [attr]
  (-> attr name (str/starts-with? "_")))


(defn reverse-lookup [^Entity entity lookup-attr]
  (let [attr          (keyword (-> lookup-attr namespace)
                               (-> lookup-attr name (subs 1)))
        target-id     (-> entity .-entity-id)
        db            (-> entity .-db)
        entities      (-> db :entity-db vals)
        is-component? (-> db :components (contains? attr))
        filter-fn     (case (-> db :schema attr)
                        :db.cardinality/one  #(-> % attr (= target-id))
                        :db.cardinality/many #(-> % attr (contains? target-id))
                        nil)
        values        (when filter-fn
                        (->> entities
                             (filter filter-fn)
                             (map #(attrs->entity db %))
                             seq))]
    (if is-component?
      (first values)
      values)))


(defn- lookup-entity
  ([entity attr] (lookup-entity entity attr nil))
  ([^Entity entity attr not-found]
   (cond
     (= :db/id attr) (-> entity .-entity-id)
     (reverse-lookup-attr? attr) (or (reverse-lookup entity attr)
                                     not-found)
     :else (if-some [v (-> entity .-attrs attr)]
             (value->entity (-> entity .-db) attr v)
             not-found))))


;;
;; Query and find:
;; ===================================================================
;;


(defn query [db match]
  (->> db
       :entity-db
       vals
       (filter (p/where-fn match))
       (map #(attrs->entity db %))
       seq))


(defn find [db match]
  (let [attrs (-> db
                  :entity-db
                  vals
                  (p/find-first match))]
    (attrs->entity db attrs)))

#?(:cljs
   (defn get-id [e attr]
     (-get-id e attr)))
